import { FC, Fragment, useState } from "react";
import { useForm } from "react-hook-form";
import useI18n from "../../hooks/useTranslations";
import { useJiraApi } from "../../http/jira/api";
import { Project, Worklog } from "../../http/jira/types";
import { downloadFileFromData, formatTimeStamp, getPreviousTrimesterDates } from "../../utils";
import Button from "../ui/Button";
import Center from "../ui/Center";
import Header from "../ui/Header";
import Input from "../ui/Input";
import LoadingSpinner from "../ui/LoadingSpinner";
import ModalDialog from "../ui/ModalDialog";
import Table from "../ui/Table";
import tableStyles from "../ui/Table.module.css";
import styles from "./Overview.module.css";

type lookup = {
  id: string;
  name: string;
};

type tableRow = {
  project: lookup;
  users: {
    user: lookup;
    logs: Worklog[];
  }[];
};

type worklog = {
  id: string;
  title: string;
  timeSpentSeconds: number;
};

type form = {
  from: string;
  to: string;
};

const OverviewReport: FC = () => {
  const i18n = useI18n();
  const jiraApi = useJiraApi();
  const [status, setStatus] = useState<{
    isLoading: boolean;
    isError: boolean;
    issues: tableRow[];
  }>({ isLoading: false, isError: false, issues: [] });
  const [worklog, setWorklog] = useState<Worklog[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  const loadData = async (from: string, to: string) => {
    setStatus((prev) => ({
      ...prev,
      isLoading: true,
    }));
    try {
      const worklogs = await jiraApi.useWorklogs(from, to);

      const grouped = worklogs.reduce(
        (acc, worklog) => {
          const { author, project } = worklog;
          // const date = time.split("T")[0]!; // Extract YYYY-MM-DD

          if (!acc.has(project.name)) {
            acc.set(project.name, {
              project: project,
              values: new Map<
                string,
                {
                  user: lookup;
                  logs: Worklog[];
                }
              >(),
            });
          }

          const userWorklogs = acc.get(project.name)!;

          if (!userWorklogs.values.has(author.id)) {
            userWorklogs.values.set(author.id, {
              user: {
                id: author.id,
                name: author.name,
              },
              logs: [],
            });
          }

          userWorklogs.values.get(author.id)?.logs.push(worklog);

          return acc;
        },
        new Map<
          string,
          {
            project: Project;
            values: Map<
              string,
              {
                user: lookup;
                logs: Worklog[];
              }
            >;
          }
        >()
      );
      // Convert the grouped map to an array of objects
      const issues: tableRow[] | undefined = grouped
        ? [...grouped].map(([_, project]) => ({
            project: project.project,
            users: [...project.values].map(([_, data]) => data),
          }))
        : [];
      setStatus((prev) => ({
        ...prev,
        isLoading: false,
        issues: issues,
      }));
    } catch (err) {
      setStatus((prev) => ({
        ...prev,
        isLoading: false,
        isError: true,
        issues: [],
      }));
    }
  };

  const trimester = getPreviousTrimesterDates();

  const {
    register,
    watch,
    trigger: triggerForm,
    formState,
  } = useForm<form>({
    mode: "onSubmit",
    defaultValues: {
      from: trimester.firstDay,
      to: trimester.lastDay,
    },
  });

  const from = watch("from");
  const to = watch("to");

  return (
    <>
      <Header title={i18n.translation.common.projectsOverview} />
      {status.isError ? (
        <div className={styles.noItemsFound}>{i18n.translation.common.noItemsFound}</div>
      ) : (
        <>
          <div className={styles.filters}>
            <Input
              type="date"
              {...register("from", { required: true })}
              isInvalid={!!formState.errors.from}
              disabled={status.isLoading}
            />
            <Input
              type="date"
              {...register("to", { required: true })}
              isInvalid={!!formState.errors.to}
              disabled={status.isLoading}
            />
            <Button
              buttonProps={{
                onClick: () =>
                  triggerForm().then((success) => {
                    success && loadData(watch("from"), watch("to"));
                  }),
                disabled: status.isLoading,
              }}
            >
              Load
            </Button>
            <Button
              buttonProps={{
                disabled: !from || !to || status.isLoading,
                onClick: async () => {
                  if (from && to) {
                    setIsExporting(true);
                    try {
                      const r = await jiraApi.exportExcel(from, to);
                      r && downloadFileFromData(r?.data, "jira_worklogs", r?.contentType);
                    } catch (err) {
                      console.log(err);
                    } finally {
                      setIsExporting(false);
                    }
                  }
                },
              }}
            >
              {i18n.translation.common.exportExcel}
            </Button>
            {isExporting && <LoadingSpinner />}
          </div>

          {status.isLoading ? (
            <Center>
              <LoadingSpinner />
            </Center>
          ) : (
            <table className={tableStyles.table}>
              {status.issues
                .sort((a, b) => a.project.name.localeCompare(b.project.name))
                .map((x, i) => (
                  <tbody key={`project_${x.project.id}`}>
                    <tr>
                      <td>
                        <b>
                          {x.project.name} (
                          {(
                            x.users.reduce((rv, u) => rv + u.logs.reduce((rv2, l) => rv2 + l.timeSpentSeconds, 0), 0) /
                            3600
                          ).toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                          })}
                          h)
                        </b>
                      </td>
                      <td></td>
                      <td></td>
                    </tr>
                    {x.users
                      ?.sort((a, b) => a.user.name.localeCompare(b.user.name))
                      .map((u) => (
                        <Fragment key={`project_${x.project.id}_user_${u.user.id}`}>
                          <tr>
                            <td>
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setWorklog(u.logs.sort((a, b) => a.time.localeCompare(b.time)));
                                }}
                              >
                                <em>
                                  {u.user.name} (
                                  {(u.logs.reduce((rv, l) => rv + l.timeSpentSeconds, 0) / 3600).toLocaleString(
                                    undefined,
                                    {
                                      maximumFractionDigits: 2,
                                    }
                                  )}{" "}
                                  h)
                                </em>
                              </a>
                            </td>
                            <td></td>
                            <td></td>
                          </tr>
                          {[
                            ...u.logs.reduce((rv, l) => {
                              const existing = rv.get(l.issue.id);
                              if (existing) {
                                existing.timeSpentSeconds += l.timeSpentSeconds;
                              } else {
                                rv.set(l.issue.id, {
                                  id: l.issue.id,
                                  title: l.issue.title,
                                  timeSpentSeconds: l.timeSpentSeconds,
                                });
                              }
                              return rv;
                            }, new Map<string, worklog>()),
                          ]
                            .sort((a, b) => a[1].title.localeCompare(b[1].title))
                            .map(([_, worklog]) => (
                              <tr key={`project_${x.project.id}_user_${u.user.id}_issue_${worklog.id}`}>
                                <td></td>
                                <td>
                                  <a
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setWorklog(u.logs.filter((l) => l.issue.id === worklog.id));
                                    }}
                                  >
                                    {worklog.title}
                                  </a>
                                </td>
                                <td align="right">
                                  {(worklog.timeSpentSeconds / 3600).toLocaleString(undefined, {
                                    maximumFractionDigits: 2,
                                  })}{" "}
                                  h
                                </td>
                              </tr>
                            ))}
                        </Fragment>
                      ))}
                    {i + 1 != status.issues.length && (
                      <tr className={styles.space} key={`project_${x.project.id}_${i}`}>
                        <td colSpan={99}></td>
                      </tr>
                    )}
                  </tbody>
                ))}
            </table>
          )}
        </>
      )}
      <ModalDialog isOpen={!!worklog.length} onClose={() => setWorklog([])} title={i18n.translation.common.worklogs}>
        <Table<Worklog>
          head={
            <tr>
              <th align="left">{i18n.translation.common.issue}</th>
              <th align="left">{i18n.translation.common.date}</th>
              <th align="left">{i18n.translation.common.time}</th>
            </tr>
          }
          renderRow={(item) => (
            <>
              <td>{item.issue.title}</td>
              <td>{formatTimeStamp(item.time)}</td>
              <td>{item.timeSpentSeconds / 3600} h</td>
            </>
          )}
          items={worklog.sort((a, b) => {
            if (!a.time) return -1;
            if (!b.time) return 1;
            return a.time.localeCompare(b.time);
          })}
        />
      </ModalDialog>
    </>
  );
};

export default OverviewReport;
